import './App.css';
import {useEffect, useState} from "react";
import {Input, Progress, Spin, Table, Tag, Tooltip, Checkbox} from "antd";
import { StarTwoTone } from '@ant-design/icons';
import axios from "axios";
import {colorFromString} from "./services/helpers";

const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_BASE_URL ||
  (process.env.NODE_ENV === "development" ?
    "http://localhost:5001/fund-trends/us-central1" :
    "https://us-central1-fund-trends.cloudfunctions.net");

const getFunds = () => {
  return axios.get(`${FUNCTIONS_BASE_URL}/getDBFunds`);
};

const getCurrentPage = () => window.location.hash === "#/formula" ? "formula" : "home";

const toNumber = (value, fallback = 0) =>
  typeof value === "number" && Number.isFinite(value) ? value : fallback;

const formatPercent = (value, digits = 1) =>
  typeof value === "number" && Number.isFinite(value) ? `${value.toFixed(digits)}%` : "-";

const formatNumber = (value, digits = 2) =>
  typeof value === "number" && Number.isFinite(value) ? value.toFixed(digits) : "-";

const formatUpdatedAt = (value) => {
  if (!value) {
    return "Unknown yet";
  }

  const updatedAt = new Date(value);
  if (Number.isNaN(updatedAt.getTime())) {
    return "Unknown yet";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Stockholm",
    timeZoneName: "short",
  }).format(updatedAt);
};

const scoreColor = (value) => toNumber(value) > 0 ? 'rgb(4, 116, 202)' : '#d0184d';

const BUY_CATEGORY_SLOTS = 3;
const HOLD_CATEGORY_RANK = 10;

const scoreFund = (fund) => {
  const oneYear = toNumber(fund.developmentOneYear);
  const threeMonths = toNumber(fund.developmentThreeMonths);
  const oneMonth = toNumber(fund.developmentOneMonth);

  return Math.round(((0.75 * oneYear) + (0.20 * threeMonths) + (0.05 * oneMonth)) * 100) / 100;
};

export const findTopFunds = (allFunds) =>
  addTradingPlan([...allFunds]
    .filter((fund) => fund.buyable !== false)
    .map((fund) => {
      return {
        ...fund,
        score: scoreFund(fund),
      };
    })
    .sort((a, b) => b.score - a.score)
    .map((fund, index) => ({
      ...fund,
      momentumRank: index,
    })));

const categoryKey = (fund) => {
  const categories = (fund.category || "")
    .split(",")
    .map((category) => category.trim())
    .filter(Boolean);

  if (categories[0] === "Branschfond" && categories[1]) {
    return categories[1];
  }

  return categories[0] || "Uncategorized";
};

const addTradingPlan = (rankedFunds) => {
  const seenCategories = new Set();
  let categoryWinnerRank = 0;

  return rankedFunds.map((fund) => {
    const category = categoryKey(fund);
    const isCategoryWinner = !seenCategories.has(category);
    if (isCategoryWinner) {
      seenCategories.add(category);
      categoryWinnerRank += 1;
    }

    if (!isCategoryWinner) {
      return {
        ...fund,
        categoryWinner: false,
        planAction: "SKIP",
        planTone: "default",
        planReason: "Category already has a stronger ranked fund",
      };
    }

    if (categoryWinnerRank <= BUY_CATEGORY_SLOTS) {
      return {
        ...fund,
        categoryWinner: true,
        categoryWinnerRank,
        planAction: "BUY",
        planTone: "green",
        planReason: `Top ${BUY_CATEGORY_SLOTS} category winner`,
      };
    }

    if (categoryWinnerRank <= HOLD_CATEGORY_RANK) {
      return {
        ...fund,
        categoryWinner: true,
        categoryWinnerRank,
        planAction: "HOLD",
        planTone: "blue",
        planReason: `Category still top ${HOLD_CATEGORY_RANK}`,
      };
    }

    return {
      ...fund,
      categoryWinner: true,
      categoryWinnerRank,
      planAction: "SELL",
      planTone: "red",
      planReason: `Category left top ${HOLD_CATEGORY_RANK}`,
    };
  });
};

function FormulaPage() {
  return (
    <div className="page-shell">
      <div className="page-header">
        <div>
          <div className="eyebrow">Formula Guide</div>
          <h1>How the momentum score works</h1>
          <p className="page-intro">
            This tool now uses the same fixed momentum formula as the backtest project, then applies the same category-based buy and sell plan. The goal is to keep the live ranking aligned with the researched strategy instead of mixing in a separate scoring model.
          </p>
        </div>
        <a className="app-nav-link" href="#/">Back to rankings</a>
      </div>

      <div className="content-stack">
        <section className="info-card">
          <h2>Score formula</h2>
          <p>
            The score is a simple weighted momentum blend. One-year performance carries most of the signal, three-month performance confirms the current trend, and one-month performance adds a small freshness check. Sharpe ratio and volatility are still displayed as context, but they are not part of this backtested score.
          </p>
        </section>

        <section className="info-card">
          <h2>Sell and buy plan</h2>
          <table className="rebalance-table">
            <thead>
            <tr>
              <th>Rule</th>
              <th>Action</th>
            </tr>
            </thead>
            <tbody>
            <tr className="action-hold">
              <td>Category winner rank 1-{BUY_CATEGORY_SLOTS}</td>
              <td>BUY</td>
            </tr>
            <tr className="action-watch">
              <td>Category winner rank {BUY_CATEGORY_SLOTS + 1}-{HOLD_CATEGORY_RANK}</td>
              <td>HOLD</td>
            </tr>
            <tr className="action-sell">
              <td>Held category leaves top {HOLD_CATEGORY_RANK}</td>
              <td>SELL</td>
            </tr>
            <tr>
              <td>Another fund in the same category ranks higher</td>
              <td>SKIP</td>
            </tr>
            </tbody>
          </table>
          <p>
            The plan follows the backtest: hold at most one fund per category, buy the top category winners, and keep existing holdings while their category remains in the top {HOLD_CATEGORY_RANK}.
          </p>
        </section>

        <section className="info-card">
          <h2>Why this strategy came out best</h2>
          <p>
            This strategy was selected after comparing several different backtest setups, formulas, and portfolio rules. The main test period was from 2017-05-27 to 2026-04-27, using Avanza fund price history and monthly rebalancing. The model used the first year of history as lookback data, then started ranking funds once 1-year, 3-month, and 1-month trend data was available.
          </p>
          <p>
            The winning version used the 75/20/5 formula with 3 portfolio slots, one fund per category, and a rule to keep holding a fund as long as its category stayed within the top 10.
          </p>
          <table className="info-table">
            <thead>
              <tr>
                <th>Backtest metric</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Strategy final value</td>
                <td>1.000x to 2.133x</td>
              </tr>
              <tr>
                <td>Strategy total return</td>
                <td>113.3%</td>
              </tr>
              <tr>
                <td>Strategy CAGR</td>
                <td>About 21.3%</td>
              </tr>
              <tr>
                <td>Benchmark</td>
                <td>Avanza Global, about 12.4% CAGR</td>
              </tr>
              <tr>
                <td>Excess CAGR</td>
                <td>About 8.9 percentage points</td>
              </tr>
            </tbody>
          </table>
          <p>
            Several other variations were tested too. These included buying the top-ranked funds directly, buying only top category winners, using a pure 1-year momentum formula, using benchmark hurdle rules, testing 2-slot and 5-slot portfolios, testing smaller fund universes, and comparing against a separate ETF dual momentum model. Some performed well, but the 3-slot category-winner version with the 75/20/5 formula produced the strongest result in the main Avanza fund test.
          </p>
        </section>

        <section className="info-card">
          <h2>Why the formula worked</h2>
          <p>
            The strategy measures the trend on the actual fund being invested in. Many trend strategies look at broad indexes, market regimes, moving averages, or macro signals. Those can be useful, but they are indirect. They describe the general market, not necessarily the fund being bought.
          </p>
          <p>
            This model instead asks which funds are already showing the strongest real performance. Each fund is scored using its own historical returns. That matters because funds inside the same market can behave very differently: one global fund may be strong while another is weak, and a sector, region, precious metals, technology, or healthcare fund can each have its own trend.
          </p>
          <p>
            The 75/20/5 weighting favors durable trends over short-term spikes. Most of the score comes from the 1-year return, the 3-month return adds confirmation, and the 1-month return gives a small check that the trend has not completely broken. This made the strategy less reactive than formulas that chase recent movement too aggressively.
          </p>
          <p>
            The category rule was also important. Without it, a ranking system can accidentally buy several funds that are basically the same bet. If five technology funds are all near the top, a simple top-funds strategy might fill the whole portfolio with technology exposure. The category-winner rule avoids that by taking only the strongest fund from each category.
          </p>
          <p>
            The hold rule reduced unnecessary trading. It did not sell immediately just because a fund dropped a few places; it kept holding while the fund's category stayed in the top 10. That gave strong trends enough time to continue.
          </p>
          <p>
            The backtest does not prove the strategy will always win in the future, but it shows that during the tested period this combination gave the best balance of return, trend capture, diversification, and practical trading rules.
          </p>
        </section>
      </div>
    </div>
  );
}

function App() {
  const [funds, setFunds] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [page, setPage] = useState(getCurrentPage);
  const [search, setSearch] = useState("");
  const [passiveOnly, setPassiveOnly] = useState(false);

  useEffect(() => {
    const onHashChange = () => setPage(getCurrentPage());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  useEffect(() => {
    getFunds()
        .then(function (response) {
          setFunds(response.data.funds || []);
          setUpdatedAt(response.data.updatedAt || null);
        })
        .catch(function (error) {
          console.log(error);
        });
  }, []);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      ellipsis: true,
      render: (name, item) => <a href={`https://www.avanza.se/fonder/om-fonden.html/${item.orderbookId}`}>{name}</a>
    },
    {
      title: 'Rank',
      dataIndex: 'momentumRank',
      key: 'momentumRank',
      width: 50,
      sorter: (a, b) => a.momentumRank - b.momentumRank,
      render: (rank) => <div>{rank + 1}</div>
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      width: 60,
      sorter: (a, b) => a.score - b.score,
      render: (score) => <div style={{color: scoreColor(score)}}>{formatNumber(score)}</div>
    },
    {
      title: 'Plan',
      dataIndex: 'planAction',
      key: 'planAction',
      width: 140,
      filters: [
        {text: 'BUY', value: 'BUY'},
        {text: 'HOLD', value: 'HOLD'},
        {text: 'SELL', value: 'SELL'},
        {text: 'SKIP', value: 'SKIP'},
      ],
      onFilter: (value, fund) => fund.planAction === value,
      render: (action, fund) => (
        <Tooltip title={fund.planReason}>
          <Tag color={fund.planTone || "default"}>{action}</Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Cat rank',
      dataIndex: 'categoryWinnerRank',
      key: 'categoryWinnerRank',
      width: 80,
      sorter: (a, b) => toNumber(a.categoryWinnerRank, 999) - toNumber(b.categoryWinnerRank, 999),
      render: (rank, fund) => fund.categoryWinner ? rank : "-",
    },
    {
      title: '1 month',
      dataIndex: 'developmentOneMonth',
      key: 'developmentOneMonth',
      width: 60,
      sorter: (a, b) => toNumber(a.developmentOneMonth) - toNumber(b.developmentOneMonth),
      render: (amount) => <div style={{color: scoreColor(amount)}}>{formatPercent(amount)}</div>
    },
    {
      title: '3 months',
      dataIndex: 'developmentThreeMonths',
      key: 'developmentThreeMonths',
      width: 60,
      sorter: (a, b) => toNumber(a.developmentThreeMonths) - toNumber(b.developmentThreeMonths),
      render: (amount) => <div style={{color: scoreColor(amount)}}>{formatPercent(amount)}</div>
    },
    {
      title: '1 year',
      dataIndex: 'developmentOneYear',
      key: 'developmentOneYear',
      width: 60,
      sorter: (a, b) => toNumber(a.developmentOneYear) - toNumber(b.developmentOneYear),
      render: (amount) => <div style={{color: scoreColor(amount)}}>{formatPercent(amount)}</div>
    },
    {
      title: 'Sharpe',
      dataIndex: 'sharpeRatio',
      key: 'sharpeRatio',
      width: 60,
      sorter: (a, b) => toNumber(a.sharpeRatio) - toNumber(b.sharpeRatio),
      render: (value) => formatNumber(value),
    },
    {
      title: 'Total fee',
      dataIndex: 'totalFee',
      key: 'totalFee',
      width: 60,
      sorter: (a, b) => toNumber(a.totalFee) - toNumber(b.totalFee),
      render: (fee) => <div>{formatPercent(fee, 2)}</div>
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 170,
      render: (categories) => <div>{categories?.split(',')
        .map((category) => category.trim())
        .filter(Boolean)
        .map((category, index) => (
          <Tag key={`${category}-${index}`} color={colorFromString(category)}>{category}</Tag>
        ))}</div>
    },
    {
      title: 'Std dev',
      dataIndex: 'standardDeviation',
      key: 'standardDeviation',
      width: 95,
      sorter: (a, b) => toNumber(a.standardDeviation) - toNumber(b.standardDeviation),
      render: (value) => formatNumber(value),
    },
    {
      title: 'Currency',
      dataIndex: 'currencyCode',
      key: 'currencyCode',
      width: 95,
      render: (currency) => currency ? <Tag color={colorFromString(currency)}>{currency}</Tag> : "-"
    },
    {
      title: 'Morning Star Rating',
      dataIndex: 'rating',
      key: 'rating',
      width: 120,
      render: (rate) => <>{[...Array(toNumber(rate)).keys()].map((star) => <StarTwoTone key={star} twoToneColor='#FFB000' />)}</>
    },
    {
      title: 'Risk',
      dataIndex: 'risk',
      key: 'risk',
      width: 110,
      render: (risk) => <Progress percent={toNumber(risk)*100/7} steps={7} showInfo={false} strokeColor="#52c41a" />
    },
    {
      title: 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
      width: 150,
      ellipsis: true,
    },
  ];

  const rankedFunds = findTopFunds(funds);
  const planBuys = rankedFunds.filter((fund) => fund.planAction === "BUY");
  const planHolds = rankedFunds.filter((fund) => fund.planAction === "HOLD");
  const planSells = rankedFunds.filter((fund) => fund.planAction === "SELL");
  const normalizedSearch = search.trim().toLowerCase();
  let filteredFunds = rankedFunds;
  if (normalizedSearch) {
    filteredFunds = filteredFunds.filter((fund) => {
      const name = (fund.name || "").toLowerCase();
      const category = (fund.category || "").toLowerCase();
      const plan = `${fund.planAction || ""} ${fund.planReason || ""}`.toLowerCase();

      return (
        name.includes(normalizedSearch) ||
        category.includes(normalizedSearch) ||
        plan.includes(normalizedSearch)
      );
    });
  }
  if (passiveOnly) {
    filteredFunds = filteredFunds.filter(fund => fund.indexFund === true);
  }

  return (
    <div className="App">
      {page === "formula" ? (
        <FormulaPage />
      ) : (
        <div className="page-shell">
          <div className="page-header">
            <div>
              <div className="eyebrow">Fund Trends</div>
              <h1>Momentum investing</h1>
              <p className="page-intro">
                Funds ranked by the backtested momentum formula, with category-based buy, hold and sell signals.
              </p>
            </div>
            <a className="app-nav-link" href="#/formula">How the formula works</a>
          </div>
          <div className="plan-summary">
            <div>
              <span>Buy candidates</span>
              <strong>{planBuys.length}</strong>
            </div>
            <div>
              <span>Hold categories</span>
              <strong>{planHolds.length}</strong>
            </div>
            <div>
              <span>Sell categories</span>
              <strong>{planSells.length}</strong>
            </div>
            <div>
              <span>Funds update</span>
              <strong>Daily 11:15 am CEST</strong>
            </div>
            <div>
              <span>Last updated</span>
              <strong>{formatUpdatedAt(updatedAt)}</strong>
            </div>
          </div>
          <div className="table-toolbar">
            <Input
              allowClear
              className="fund-search"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search fund name, category or plan"
              value={search}
            />
            <Checkbox
              checked={passiveOnly}
              onChange={(e) => setPassiveOnly(e.target.checked)}
            >
              Passive only
            </Checkbox>
          </div>
          <div className="table-shell">
            {funds?.length > 0 ? <Table dataSource={filteredFunds} columns={columns} rowKey={(fund) => fund.orderbookId || fund.name} pagination={{pageSize: 100, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"]}} scroll={{x: "max-content"}} size="small" /> : <Spin size="large" />}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
